import type { AdminCustomerCart, Scan, ScanReviewGroup, StoreCustomer } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { idleLoad, type LoadStatus } from "../../core/primitives/load-status";
import { confirmedScans, type ReviewDraft } from "../../core/primitives/review-row";
import type { Capture } from "../../core/services/scan-session";

/** A customer's scans no card was recognised in: the review queue leaves them out, so they are listed from the scans. */
export const unrecognisedOf = (scans: readonly Scan[]): Scan[] =>
  scans.filter((s) => s.recognitionOutcome === "no_match");

/** Rows to review, scans no card was recognised in, or scans the customer can buy; else the empty state. */
export const customerListed = (reviewGroups: number, scans: readonly Scan[]): boolean =>
  reviewGroups > 0 || unrecognisedOf(scans).length > 0 || confirmedScans(scans).length > 0;

export class CustomerScansState {
  /** The selected shop, or null until it is known. */
  readonly storeId = new Rx<string | null>(null);
  readonly customersStatus = new Rx<LoadStatus>(idleLoad);
  readonly customers = new RxList<StoreCustomer>();
  /** The customer drilled into, when the route names one. */
  readonly selectedCustomer = new Rx<StoreCustomer | null>(null);
  readonly scansStatus = new Rx<LoadStatus>(idleLoad);
  readonly scans = new RxList<Scan>();
  /** The customer's pending scans as review rows: the queue's groups cut down to theirs. */
  readonly reviewGroups = new RxList<ScanReviewGroup>();
  /** What the reviewer typed on each row, by `customerRowKey`. */
  readonly drafts = new Rx<Readonly<Record<string, ReviewDraft>>>({});
  /** The row a confirm or reject is running for (`customerRowKey`); null when idle. */
  readonly busyKey = new Rx<string | null>(null);
  /** The customers' review queue did not answer on the last read: rows stand alone, without the card they matched. */
  readonly queueFailed = new Rx(false);
  /** The candidate a pick is running for. */
  readonly picking = new Rx<{ key: string; productId: string } | null>(null);
  /** Open carts at this counter, for the Carts tab's count. */
  readonly openCarts = new Rx(0);
  /** The customer list's badges: each customer's open carts, summed. */
  readonly cartTotals = new Rx<ReadonlyMap<string, number>>(new Map());
  /** The selected customer's open cart, if they have one. */
  readonly cart = new Rx<AdminCustomerCart | null>(null);
  /** This browser's cards scanned for the customer still on their way, newest first, and those landed until read. */
  readonly captures = new RxList<Capture>();
  /** Until the cart has loaded, Add to cart could open a second one for a customer who has one. */
  readonly cartStatus = new Rx<LoadStatus>(idleLoad);
  /** Checkout, open over the page for one cart: that cart as last read, null once it is gone. */
  readonly checkout = new Rx<{ readonly cartId: string; readonly cart: AdminCustomerCart | null } | null>(null);
  /** The scan an add or remove is running for. */
  readonly busyScanId = new Rx<string | null>(null);
  readonly actionError = new Rx<string | null>(null);
  /** The row whose action it answers, said under it: a row to review by its key, a confirmed scan by its id. */
  readonly actionErrorAt = new Rx<string | null>(null);
  /** The confirmed scan the reviewer is asked about deleting; null when no question is open. */
  readonly deleting = new Rx<Scan | null>(null);
  /** The "Add customer" dialog, open when not null. */
  readonly newCustomer = new Rx<NewCustomerDraft | null>(null);

  constructor(readonly customerId: string | null) {}
}

export interface NewCustomerDraft {
  readonly name: string;
  readonly busy: boolean;
  /** What is wrong with the name. */
  readonly error: string | null;
  /** Why the customer was not made, when the name is not the reason. */
  readonly failure: string | null;
}

export abstract class CustomerScansActions {
  abstract selectCustomer(id: string): void;
  abstract showAllCustomers(): void;
  abstract editScan(scan: Scan): void;
  /** A confirmed scan with a card opens that card, with what the customer was quoted; any other, its review. */
  abstract openScan(scan: Scan): void;
  abstract askDelete(scan: Scan): void;
  abstract cancelDelete(): void;
  /** Deletes the scan asked about: its quote goes too, and it comes out of the customer's cart. */
  abstract deleteScan(): Promise<void>;
  abstract setPrice(group: ScanReviewGroup, price: string): void;
  /** Confirms the row's scans with its price; a customer's scan stocks nothing. */
  abstract confirm(group: ScanReviewGroup): Promise<void>;
  abstract reject(group: ScanReviewGroup): Promise<void>;
  abstract pick(group: ScanReviewGroup, productId: string): Promise<void>;
  abstract retry(): void;
  abstract addToCart(scan: Scan): Promise<void>;
  abstract removeFromCart(scan: Scan): Promise<void>;
  /** Opens the customer's cart in place, as iOS's Checkout sheet. */
  abstract checkout(): void;
  abstract closeCheckout(): void;
  abstract scanForCustomer(): void;
  /** A card whose scan failed, sent again. */
  abstract retryCapture(id: string): void;
  /** A card whose scan failed, dropped. */
  abstract discardCapture(id: string): void;
  abstract dismissActionError(): void;
  abstract openNewCustomer(): void;
  abstract setNewCustomerName(name: string): void;
  abstract closeNewCustomer(): void;
  abstract createCustomer(): Promise<void>;
}
